// src/components/trip/MemberList.tsx
import React from 'react';
import { 
  View, 
  Text, 
  Card, 
  Collection, 
  Flex, 
  Badge,
  CheckboxField
} from '@aws-amplify/ui-react';
import { User, Shield, XCircle } from 'lucide-react';
import { TripRole } from '../../API';

interface MemberListProps {
  members: any[];
  currentUserId?: string;
  isSelectionMode?: boolean;
  selectedMembers?: Set<string>;
  onMemberSelect?: (memberId: string, event: React.MouseEvent) => void;
  isCreator?: boolean;
}

const MemberList: React.FC<MemberListProps> = ({ 
  members, 
  currentUserId,
  isSelectionMode = false,
  selectedMembers = new Set(),
  onMemberSelect,
  isCreator = false
}) => {
  if (members.length === 0) {
    return (
      <View textAlign="center" padding="3rem" backgroundColor="#202020" borderRadius="medium">
        <Text fontSize="1.2rem" color="white">No members in this trip yet.</Text>
      </View>
    );
  }

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'CREATOR':
        return "#F59E0B"; // Amber color for creators
      case TripRole.PARTICIPANT:
        return "#0073EA"; // Blue for participants
      case TripRole.VIEWER:
        return "#666"; // Gray for viewers
      default:
        return "#666";
    }
  };
  
  return (
    <Collection
      type="list"
      items={members}
      gap="1rem"
    >
      {(member, index) => (
        <Card 
          key={index} 
          padding="1rem" 
          variation="elevated"
          backgroundColor="#1A1A1A"
          borderColor={selectedMembers.has(member.id) ? "#3B82F6" : "#303030"}
          borderWidth={selectedMembers.has(member.id) ? "2px" : "1px"}
          className={`member-card ${isSelectionMode ? 'selection-mode' : ''}`}
          onClick={isSelectionMode && onMemberSelect ? (e) => onMemberSelect(member.id, e) : undefined}
          style={{ 
            position: 'relative',
            cursor: (isSelectionMode && isCreator && member.userId !== currentUserId) ? 'pointer' : 'default'
          }}
        >
          {/* Selection checkbox - only show for non-current user */}
          {isSelectionMode && isCreator && member.userId !== currentUserId && (
            <View 
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                zIndex: 5,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '50%',
                padding: '4px'
              }}
            >
              <CheckboxField
                name={`member-select-${member.id}`}
                checked={selectedMembers.has(member.id)}
                onChange={(e) => onMemberSelect && onMemberSelect(member.id, e as unknown as React.MouseEvent)}
                onClick={(e) => e.stopPropagation()}
                label=""
              />
            </View>
          )}

          <Flex direction="row" alignItems="center">
            <View
              style={{
                backgroundColor: "rgba(0, 115, 234, 0.2)",
                padding: "1rem",
                borderRadius: "50%",
                marginRight: "1rem"
              }}
            >
              {member.role === 'CREATOR' ? (
                <Shield size={24} color="#F59E0B" />
              ) : (
                <User size={24} color="#0073EA" />
              )}
            </View>
            
            <Flex direction="column">
              <Flex alignItems="center" gap="0.5rem">
                <Text fontWeight="bold" color="white">
                  {member.userName || "User"}
                </Text>
                
                {member.userId === currentUserId && (
                  <Badge
                    style={{
                      backgroundColor: "#333",
                      color: "white",
                      fontSize: "0.7rem"
                    }}
                  >
                    YOU
                  </Badge>
                )}
              </Flex>
              
              <Flex gap="0.5rem" marginTop="0.5rem">
                <Badge
                  style={{
                    backgroundColor: getRoleBadgeColor(member.role),
                    color: "white"
                  }}
                >
                  {member.role}
                </Badge>
                
                <Text fontSize="0.8rem" color="#aaa">
                  Joined {new Date(member.createdAt).toLocaleDateString()}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      )}
    </Collection>
  );
};

export default MemberList;